import React from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";

interface FilterButtonProps {
  label: string;
  isActive: boolean;
  isExpanded: boolean;
  onClick: () => void;
  onRemove?: () => void;
  icon?: React.ReactNode;
  badge?: string | number;
}

/**
 * Presentational component for a filter button.
 * Displays a filter key with active/inactive states.
 */
export function FilterButton({
  label,
  isActive,
  isExpanded,
  onClick,
  onRemove,
  icon,
  badge,
}: FilterButtonProps) {
  const baseClasses =
    "px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 border";

  const activeClasses = isActive
    ? "bg-primary/10 text-primary border-primary/30 shadow-md hover:bg-primary/20"
    : "bg-card text-muted-foreground border-border hover:bg-accent hover:border-border";

  const expandedClasses = isExpanded ? "ring-2 ring-offset-2 ring-ring" : "";

  return (
    <button onClick={onClick} className={`${baseClasses} ${activeClasses} ${expandedClasses}`}>
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{label}</span>
      {badge !== undefined && (
        <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-muted text-muted-foreground">
          {badge}
        </span>
      )}
      {isActive && onRemove && (
        <span
          role="button"
          tabIndex={0}
          aria-label="Remove filter"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              e.preventDefault();
              onRemove();
            }
          }}
          className="ml-1 hover:bg-black/10 rounded-full p-0.5"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </span>
      )}
    </button>
  );
}
